import { DataTable, ExtendedColumnDef } from "components/Datatable";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { IBalanceReport } from "src/@types/balance-report";
import { Meta } from "src/@types/paginated";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getPagesToShow } from "../../../../../../utils/pagination";
import { useFilterActions } from "../../../ducks/filter/hooks/actions";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";

interface ITableProps<T> {
  data: IBalanceReport[];
  meta: Meta;
  columns: ExtendedColumnDef<T>[];
  isFetching?: boolean;
}

export default function Table({
  data,
  meta,
  columns,
  isFetching = false,
}: ITableProps<IBalanceReport>) {
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const selectedPage = (page: number | string) => {
    if (page === "...") return "ignore";
    if (!meta.currentPage && page === 1) return "selected";
    return page === meta.currentPage ? "selected" : "";
  };

  return (
    <>
      <div className="bg-white flex flex-col overflow-x-auto">
        <DataTable
          data={data as any}
          columns={columns}
          emptyMessage="Selecione uma data de conciliação para ver os dados."
          isLoading={isFetching}
        />
        <div className="flex w-full items-center justify-between pagination-container">
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Linhas por página</p>
              <Select
                value={`${filters.meta?.perPage || 10}`}
                onValueChange={(value) => {
                  updateFilter({
                    key: "meta",
                    value: {
                      ...filters.meta,
                      perPage: parseInt(value, 10),
                      page: 1,
                    },
                  });
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={filters.meta?.perPage || 10} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 25, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex text-sm">
              <button
                className={`cursor-pointer px-3 py-1 bg-[#2b0c1a] text-white rounded-l-lg rounded-tl-none ${
                  meta.prev && !isFetching ? "" : "text-[#8d91a2] bg-[#d9dae0] cursor-not-allowed"
                }`}
                onClick={() =>
                  updateFilter({
                    key: "meta",
                    value: { ...filters.meta, page: (filters.meta?.page || 1) - 1 || 1 },
                  })
                }
                disabled={!meta.prev || isFetching}
              >
                Anterior
              </button>
              {getPagesToShow(meta).map((page: any) => (
                <span
                  key={page}
                  className={`flex justify-center px-2 py-2 border border-[#dcdce3] border-l-0 border-t-0 min-w-[29px] ${
                    selectedPage(page) === "selected" ? "bg-[#f4c83e]" : ""
                  } ${page !== "..." ? "cursor-pointer hover:underline" : ""}`}
                  onClick={() => {
                    if (typeof page === "number") {
                      updateFilter({
                        key: "meta",
                        value: { ...filters.meta, page: page },
                      });
                    }
                  }}
                >
                  {page}
                </span>
              ))}
              <button
                className={`cursor-pointer px-3 py-1 bg-[#2b0c1a] text-white rounded-r-lg rounded-tr-none ${
                  meta.next && !isFetching ? "" : "text-[#8d91a2] bg-[#d9dae0] cursor-not-allowed"
                }`}
                onClick={() =>
                  updateFilter({
                    key: "meta",
                    value: { ...filters.meta, page: (filters.meta?.page || 1) + 1 || 1 },
                  })
                }
                disabled={!meta.next || isFetching}
              >
                Próximo
              </button>
            </div>
          </div>
          <span className="text-sm font-light leading-4 text-[#444444]">{`${meta.currentPage ?? 0} de ${
            meta.lastPage ?? 0
          } página${meta?.total !== 1 ? "s" : ""}`}</span>
        </div>
      </div>
    </>
  );
}